/**
 * Reactive-variable store for the pipeline Stage editor (SE-5488).
 *
 * Holds the editor's open/closed state and the board it is scoped to. Uses
 * Apollo `makeVar` to match the frontend-v2 store convention (mirrors
 * `rulesPanelStore`), so any toolbar surface can open the editor without
 * prop-drilling a callback.
 * @module features/player-kanban/stores/stageEditorStore
 */

import { makeVar } from "@apollo/client";

/** Serializable state of the Stage editor. */
export interface StageEditorState {
  /** Whether the editor is open. */
  readonly open: boolean;
  /**
   * Pipeline the editor is scoped to. Stages are per-board, so a board id is
   * required to fetch/mutate; null means the editor is closed / unscoped.
   */
  readonly kanbanBoardId: string | null;
}

/** Initial (closed) state. */
const INITIAL_STATE: StageEditorState = {
  open: false,
  kanbanBoardId: null,
};

/**
 * Apollo reactive variable holding the Stage-editor state.
 * @remarks Subscribe via `useReactiveVar(stageEditorVar)` (or the
 * `useStageEditorPanelState` hook) and update through the helper below.
 */
export const stageEditorVar = makeVar<StageEditorState>(INITIAL_STATE);

/** Reset the editor to its initial closed state. */
export function resetStageEditor(): void {
  stageEditorVar(INITIAL_STATE);
}
